import { useEffect } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { useAssetsStore } from "../../../../store/assetsStore";
import { useRoomStore } from "../../model/roomStore";
import { RoomCard } from "../RoomCard/RoomCard";
import css from "./RoomSelect.module.scss";

/**
 * Верхний слой приложения: пока комната не выбрана, экран перекрывает лоадер и
 * игру. Ассеты в это время уже греются (см. `useAssetsStore`), поэтому к
 * моменту выбора игра чаще всего готова и лоадер не показывается вовсе.
 *
 * Список комнат приходит от каталога лобби и показывается в порядке ответа.
 * Клиент не строит его сам и не подставляет комнату по умолчанию (§7).
 */
export const RoomSelect = () => {
  const { t } = useTranslation();
  const status = useRoomStore((state) => state.status);
  const rooms = useRoomStore((state) => state.rooms);
  const loadRooms = useRoomStore((state) => state.loadRooms);
  const selectRoom = useRoomStore((state) => state.selectRoom);
  const progress = useAssetsStore((state) => state.progress);
  const isReady = useAssetsStore((state) => state.isReady);

  useEffect(() => {
    if (status === "idle") void loadRooms();
  }, [status, loadRooms]);

  const progressPercent = Math.round((isReady ? 1 : progress) * 100);

  return (
    <div className={css.overlay}>
      <div className={css.inner}>
        <header className={css.header}>
          <h1 className={css.title}>{t("rooms.title")}</h1>
          <p className={css.subtitle}>{t("rooms.subtitle")}</p>
        </header>

        {status === "loading" || status === "idle" ? (
          <p className={css.state}>{t("rooms.loading")}</p>
        ) : null}

        {status === "error" ? (
          <div className={css.errorBox}>
            <p className={css.errorText}>{t("rooms.unavailable")}</p>
            <button
              type="button"
              className={css.retry}
              onClick={() => void loadRooms()}
            >
              {t("rooms.retry")}
            </button>
          </div>
        ) : null}

        {status === "ready" ? (
          <div className={css.grid}>
            {rooms.map((room, index) => (
              <RoomCard
                key={room.roomId}
                room={room}
                index={index}
                onSelect={selectRoom}
              />
            ))}
          </div>
        ) : null}

        <footer className={css.footer}>
          <div
            className={css.progressTrack}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercent}
          >
            <div
              className={css.progressBar}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className={css.progressLabel}>
            {isReady
              ? t("rooms.assets_ready")
              : t("rooms.assets_loading", { percent: progressPercent })}
          </span>
        </footer>
      </div>
    </div>
  );
};
